import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { TFunction } from "i18next"

import { Activity, Coins, GraduationCap, Users } from "@/components/icons"
import { ModulePageHeader } from "@/components/layouts/module-page-header"
import { EmptyState } from "@/components/ui/empty-state"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { KPI } from "@/features/admin-dashboard/components/admin-dashboard-shared"
import { CostEstimateCard } from "@/features/admin-dashboard/components/cost-estimate-card"
import { CostsByUserTable } from "@/features/admin-dashboard/components/costs-by-user-table"
import { TrainingStatusCard } from "@/features/admin-dashboard/components/training-status-card"
import {
  type DateRangePresetId,
  getDateRangePresets,
  getPresetById,
  resolveDateRange,
} from "@/features/admin-dashboard/utils/date-ranges"
import { AssignedUserVisitsCard } from "@/features/local-ai-lead/components/assigned-user-visits-card"
import { LeadAdoptionCurve } from "@/features/local-ai-lead/components/lead-adoption-curve"
import { LeadHeadcountCard } from "@/features/local-ai-lead/components/lead-headcount-card"
import { LeadRequestsCard } from "@/features/local-ai-lead/components/lead-requests-card"
import { SiteMonthlyCostTable } from "@/features/local-ai-lead/components/site-monthly-cost-table"
import {
  useLocalAiLeadCompanyUsageQuery,
  useLocalAiLeadCostBreakdownQuery,
  useLocalAiLeadCostsQuery,
  useLocalAiLeadHeadcountMutation,
  useLocalAiLeadMeQuery,
  useLocalAiLeadSummaryQuery,
  useLocalAiLeadTrainingQuery,
  useLocalAiLeadTrendQuery,
} from "@/features/local-ai-lead/queries"

function requestsPerUserSub(
  t: TFunction,
  values: {
    current: number | null | undefined
    average: number | null | undefined
    rangeDays: number | null | undefined
  }
): string | undefined {
  // `requestsPerUser` is the selected window's total; `requestsPerUser3m` is
  // already a monthly rate. Scale the window to 30 days before comparing.
  const { current, average, rangeDays } = values
  if (typeof current !== "number") return undefined
  if (typeof average !== "number") return undefined
  const days = typeof rangeDays === "number" && rangeDays > 0 ? rangeDays : 30
  const monthly = current * (30 / days)
  const avg = average.toFixed(1)
  if (monthly === average) {
    return t(
      "localAiLeadPage.requestsPerUserTrend.flat",
      "In line with the 3-month average {{avg}}",
      {
        avg,
      }
    )
  }
  if (monthly > average) {
    return t("localAiLeadPage.requestsPerUserTrend.up", "Above the 3-month average {{avg}}", {
      avg,
    })
  }
  return t("localAiLeadPage.requestsPerUserTrend.down", "Below the 3-month average {{avg}}", {
    avg,
  })
}

function formatCurrency(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value)
  } catch {
    return `${value.toFixed(2)} ${currency}`
  }
}

function formatKpiCurrency(value: number, currency: string): string {
  try {
    // KPI's count-up parser expects an en-US numeric string. Detailed cost
    // cards below retain the user's locale and cents.
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value)
  } catch {
    return `${Math.round(value)} ${currency}`
  }
}

export function LocalAiLeadPage() {
  const { t } = useTranslation()

  const [selectedPresetId, setSelectedPresetId] = useState<DateRangePresetId>("last30Days")
  const [currency, setCurrency] = useState<"SEK" | "USD" | "EUR">("USD")
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)

  const dateRange = useMemo(
    () => resolveDateRange({ type: "preset", presetId: selectedPresetId }),
    [selectedPresetId]
  )

  const meQuery = useLocalAiLeadMeQuery()
  const tenantSharedModel = meQuery.data?.costModel === "shared_annual_v1"
  const enabledMonth = meQuery.data?.costModelEnabledFrom?.slice(0, 7)
  const selectedCostMonth = dateRange.endDate.slice(0, 7)
  const isSharedModel = tenantSharedModel && (!enabledMonth || selectedCostMonth >= enabledMonth)
  const assignedGroups = meQuery.data?.assignedGroups ?? []
  const groupValue = selectedGroup ?? (assignedGroups.length === 1 ? assignedGroups[0] : undefined)
  const reportingCurrency = isSharedModel ? (meQuery.data?.reportingCurrency ?? "USD") : currency
  const legacyQueriesEnabled = meQuery.isSuccess && !isSharedModel
  const summaryQuery = useLocalAiLeadSummaryQuery(dateRange, currency, legacyQueriesEnabled)
  const costsQuery = useLocalAiLeadCostsQuery(
    { startDate: dateRange.startDate, endDate: dateRange.endDate },
    currency,
    legacyQueriesEnabled
  )
  const trainingQuery = useLocalAiLeadTrainingQuery(meQuery.isSuccess && !tenantSharedModel)
  const trendQuery = useLocalAiLeadTrendQuery(dateRange, currency, legacyQueriesEnabled)
  const costBreakdownQuery = useLocalAiLeadCostBreakdownQuery(dateRange, {
    currency: reportingCurrency,
    groupValue,
    enabled: meQuery.isSuccess && (!isSharedModel || !!groupValue),
  })
  const companyUsageQuery = useLocalAiLeadCompanyUsageQuery(dateRange, groupValue, isSharedModel)
  const headcountMutation = useLocalAiLeadHeadcountMutation()

  const summary = summaryQuery.data ?? null
  const costs = costsQuery.data ?? null
  const companyUsage = companyUsageQuery.data ?? null
  const training = trainingQuery.data ?? null
  const costBreakdown = costBreakdownQuery.data ?? null

  const trendData = useMemo(() => {
    const points = isSharedModel ? companyUsage?.points : trendQuery.data?.points
    if (!points) return null
    return points.map((point) => {
      const date = new Date(point.date)
      const label = date.toLocaleDateString("sv-SE", { day: "numeric", month: "short" })
      return {
        label,
        cost: point.costUSD,
        tokensTotal: point.tokensTotal,
        requests: point.requests,
      }
    })
  }, [companyUsage?.points, isSharedModel, trendQuery.data])

  // Show skeleton while fetching Local AI Lead identity
  if (meQuery.isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    )
  }

  // Error state if Local AI Lead identity fetch failed
  if (meQuery.isError) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center p-6">
        <EmptyState
          icon={Users}
          title={t("localAiLeadPage.errorTitle", "Something went wrong")}
          description={t(
            "localAiLeadPage.errorDescription",
            "Could not load Local AI Lead data. Please try again later."
          )}
        />
      </div>
    )
  }

  // Route visibility is enforced server-side via the `ambassador_has_mapping`
  // user_scope predicate (string preserved for DB compatibility) — unmapped
  // users never see this route in their config. A Local AI Lead with zero
  // assigned users (provisioned but unassigned) still sees the route, so we
  // render a friendly empty state instead of a page full of zeroed KPIs.
  const hasNoAssignedUsers = isSharedModel
    ? assignedGroups.length === 0
    : (meQuery.data?.assignedUsers.length ?? 0) === 0
  if (meQuery.data && hasNoAssignedUsers) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center p-6">
        <EmptyState
          icon={Users}
          title={t("localAiLeadPage.noUsersTitle", "No Users Assigned")}
          description={t(
            "localAiLeadPage.noUsersDescription",
            "You haven't been assigned any users yet. Contact your administrator."
          )}
        />
      </div>
    )
  }

  const sections = costBreakdown?.sections
  const activeCurrency = isSharedModel
    ? (costBreakdown?.currency ?? reportingCurrency)
    : (summary?.currency ?? costs?.currency ?? currency)
  const activePresetLabel = getPresetById(selectedPresetId)?.labelKey
    ? t(getPresetById(selectedPresetId)!.labelKey)
    : selectedPresetId

  let totalCostKpi = "—"
  let activeUsersKpi = "—"
  let trainingKpi = "—"
  let requestsKpi = "—"
  if (isSharedModel) {
    if (costBreakdown) {
      totalCostKpi = formatKpiCurrency(costBreakdown.monthlyCostTotal, activeCurrency)
      activeUsersKpi = String(costBreakdown.siteActiveUsers ?? "—")
    }
    if (companyUsage) {
      trainingKpi = `${companyUsage.avgTrainingPct.toFixed(1)}%`
      requestsKpi = companyUsage.summary.totalRequests.toLocaleString("sv-SE")
    }
  } else if (summary) {
    totalCostKpi = formatCurrency(summary.totalCost, activeCurrency)
    activeUsersKpi = String(summary.activeUsers)
    trainingKpi = `${summary.avgTrainingPct.toFixed(1)}%`
    requestsKpi = summary.totalRequests.toLocaleString("sv-SE")
  }

  return (
    <div className="space-y-6 p-6">
      <ModulePageHeader
        className="gap-4 p-0"
        subtitle={t("localAiLeadPage.description")}
        title={t("localAiLeadPage.title")}
        actions={
          <>
            <Select
              value={selectedPresetId}
              onValueChange={(value) => setSelectedPresetId(value as DateRangePresetId)}
            >
              <SelectTrigger aria-label={activePresetLabel} className="w-44 rounded-xl">
                <SelectValue placeholder={t("adminDashboard.page.periodSelector.placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {getDateRangePresets()
                  .filter((preset) => !isSharedModel || preset.id !== "last365Days")
                  .map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      {t(preset.labelKey)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {isSharedModel && assignedGroups.length > 1 ? (
              <Select value={groupValue} onValueChange={setSelectedGroup}>
                <SelectTrigger
                  aria-label={t("localAiLeadPage.selectCompany", "Select company")}
                  className="w-48 rounded-xl"
                >
                  <SelectValue placeholder={t("localAiLeadPage.selectCompany", "Select company")} />
                </SelectTrigger>
                <SelectContent>
                  {assignedGroups.map((group) => (
                    <SelectItem key={group} value={group}>
                      {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            {!isSharedModel ? (
              <Select
                value={currency}
                onValueChange={(value) => setCurrency(value as "SEK" | "USD" | "EUR")}
              >
                <SelectTrigger className="w-28 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SEK">SEK</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            ) : null}
          </>
        }
      />

      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI
          icon={Coins}
          isLoading={isSharedModel ? costBreakdownQuery.isLoading : summaryQuery.isLoading}
          value={totalCostKpi}
          label={
            isSharedModel
              ? t("localAiLeadPage.totalSiteMonthlyCost", "Total company cost · monthly")
              : t("localAiLeadPage.totalCost")
          }
        />
        <KPI
          icon={Users}
          isLoading={isSharedModel ? costBreakdownQuery.isLoading : summaryQuery.isLoading}
          label={t("localAiLeadPage.activeUsers")}
          value={activeUsersKpi}
        />
        <KPI
          icon={GraduationCap}
          isLoading={isSharedModel ? companyUsageQuery.isLoading : summaryQuery.isLoading}
          label={t("localAiLeadPage.avgTraining")}
          value={trainingKpi}
        />
        <KPI
          icon={Activity}
          isLoading={isSharedModel ? companyUsageQuery.isLoading : summaryQuery.isLoading}
          label={t("localAiLeadPage.totalRequests")}
          value={requestsKpi}
        />
        {!isSharedModel && sections?.requests_per_user ? (
          <KPI
            icon={Activity}
            isLoading={costBreakdownQuery.isLoading}
            label={t("localAiLeadPage.requestsPerUser", "Requests per user")}
            sub={requestsPerUserSub(t, {
              current: costBreakdown?.requestsPerUser,
              average: costBreakdown?.requestsPerUser3m,
              rangeDays: costBreakdown?.rangeDays,
            })}
            value={
              typeof costBreakdown?.requestsPerUser === "number"
                ? costBreakdown.requestsPerUser.toFixed(1)
                : "—"
            }
          />
        ) : null}
      </div>

      {/* Requests waiting — the lead's approval queue (fair use round 2,
          #3087). Above the cost cards on purpose: it is the only thing on this
          page with someone waiting at the other end of it, and it renders
          nothing at all when the platform has delegated no allowance. */}
      <LeadRequestsCard />

      {isSharedModel && costBreakdownQuery.isError ? (
        <p className="text-destructive text-sm" role="alert">
          {t(
            "localAiLeadPage.costUnavailable",
            "Company cost is unavailable. Ask an administrator to check the cost ledger and monthly exchange rate."
          )}
        </p>
      ) : null}
      {isSharedModel && companyUsageQuery.isError ? (
        <p className="text-destructive text-sm" role="alert">
          {t(
            "localAiLeadPage.usageUnavailable",
            "Company usage for this period is unavailable. An administrator may need to add historical exchange rates."
          )}
        </p>
      ) : null}

      {/* Monthly cost estimate */}
      <CostEstimateCard
        data={costBreakdown}
        formatCurrency={(value) => formatCurrency(value, activeCurrency)}
        isLoading={costBreakdownQuery.isLoading}
        allocatedSharedMonthly={
          sections?.shared_cost_lines ? costBreakdown?.allocatedSharedMonthly : undefined
        }
      />

      {isSharedModel && costBreakdown?.costMonth ? (
        <p className="text-muted-foreground text-sm">
          {t(
            "localAiLeadPage.costMonthLabel",
            "Cost month: {{month}}. The period selector controls usage views.",
            { month: costBreakdown.costMonth }
          )}
        </p>
      ) : null}

      {sections?.headcount_adoption ? (
        <LeadHeadcountCard
          isSaving={headcountMutation.isPending}
          rows={costBreakdown?.headcounts ?? []}
          onSave={(groupValue, employees) => {
            headcountMutation.mutate({ groupValue, employees })
          }}
        />
      ) : null}

      {sections?.adoption_curve && costBreakdown?.adoptionCurve ? (
        <LeadAdoptionCurve
          curve={costBreakdown.adoptionCurve}
          formatCurrency={(value) => formatCurrency(value, activeCurrency)}
        />
      ) : null}

      {meQuery.data?.assignedVisitsEnabled && groupValue ? (
        <AssignedUserVisitsCard enabled groupValue={groupValue} mode="lead" />
      ) : null}

      {/* Costs section */}
      <div id="local-ai-lead-costs">
        {isSharedModel && costBreakdown?.costMonth && sections?.user_full_cost ? (
          <SiteMonthlyCostTable
            costMonth={costBreakdown.costMonth}
            currency={activeCurrency}
            rows={costBreakdown.siteUserCosts ?? []}
          />
        ) : null}
        <CostsByUserTable
          data={isSharedModel ? companyUsage : costs}
          isLoading={isSharedModel ? companyUsageQuery.isLoading : costsQuery.isLoading}
          isTrendLoading={isSharedModel ? companyUsageQuery.isLoading : trendQuery.isLoading}
          trendCurrency={isSharedModel ? reportingCurrency : currency}
          trendData={trendData}
          sharedPerUserMonthly={
            !isSharedModel && sections?.user_full_cost
              ? costBreakdown?.sharedPerUserMonthly
              : undefined
          }
        />
      </div>

      {/* Training section */}
      {!tenantSharedModel ? (
        <div id="local-ai-lead-training">
          <TrainingStatusCard
            data={training}
            isFetching={trainingQuery.isFetching}
            isLoading={trainingQuery.isLoading}
          />
        </div>
      ) : null}
    </div>
  )
}
